import type { QueryClient } from '@tanstack/react-query';
import { supportPollingRetry, supportQueryRetry } from '../../utils/Support/supportHelpers';

export enum SupportTicketFilterProperty {
	Status = 'Status',
	Priority = 'Priority',
	Channel = 'Channel',
	Mailbox = 'Mailbox',
	Assignee = 'Assignee',
	ContactEmail = 'ContactEmail',
	/** Case-insensitive "contains" across the ticket's contact name and email. */
	Contact = 'Contact',
	Account = 'Account',
	Category = 'Category',
}

export type SupportTicketFilterOperator = 'And' | 'Or' | 'Not';

export interface SupportTicketFilterCriteria {
	criteria?: SupportTicketFilterCriteria[];
	op?: SupportTicketFilterOperator;
	property?: SupportTicketFilterProperty;
	value?: string;
}

export interface SupportTicketFilterRequest {
	criteria?: SupportTicketFilterCriteria[];
	op?: Extract<SupportTicketFilterOperator, 'And'>;
	/**
	 * CSM refinement. Not a criterion: a ticket carries no CSM (it lives on the customer's account),
	 * so the server resolves this to that CSM's accounts and narrows by them.
	 */
	csmId?: string;
}

export interface SupportTicketListParams {
	filterRequest: SupportTicketFilterRequest;
	pageSize: number;
	sort?: 'asc' | 'desc';
	sortBy?: string;
}

export interface SupportTicketListRequestParams extends SupportTicketListParams {
	pageToken?: string;
}

/**
 * Free-text + qualifier search, mirroring the `support/tickets/search` endpoint params. The query
 * runs against the server's full-text index (customer email, subject, and message content); the
 * qualifiers are exact id/enum matches that narrow the results. `mailboxId` scopes the search to
 * one mailbox (validated server-side against the agent's memberships); omitted, it spans them all.
 */
export interface SupportTicketSearchParams {
	query?: string;
	mailboxId?: string;
	accountId?: string;
	status?: string;
	priority?: string;
	categoryId?: string;
	assigneeId?: string;
	csmId?: string;
	page: number;
	pageSize: number;
}

function encodePathSegment(segment: string) {
	return encodeURIComponent(segment);
}

function appendIfPresent(queryParams: URLSearchParams, key: string, value: string | number | undefined) {
	if (value) queryParams.set(key, String(value));
}

function withQueryString(path: string, queryParams: URLSearchParams) {
	const queryString = queryParams.toString();
	return queryString ? `${path}?${queryString}` : path;
}

const supportTicketsPath = (ticketId: string) => `support/tickets/${encodePathSegment(ticketId)}`;
const supportTicketThreadsPath = (ticketId: string) => `${supportTicketsPath(ticketId)}/threads`;
const supportTicketThreadPath = (ticketId: string, threadId: string) =>
	`${supportTicketThreadsPath(ticketId)}/${encodePathSegment(threadId)}`;
const supportTicketGitHubIssuesPath = (ticketId: string) => `${supportTicketsPath(ticketId)}/github/issues`;
const supportTicketGitHubIssuePath = (ticketId: string, linkId: string) =>
	`${supportTicketGitHubIssuesPath(ticketId)}/${encodePathSegment(linkId)}`;
const supportMailboxPath = (mailboxId: string) => `support/mailboxes/${encodePathSegment(mailboxId)}`;
const supportMailboxCategoriesPath = (mailboxId: string) => `${supportMailboxPath(mailboxId)}/categories`;
const supportMailboxAgentsPath = (mailboxId: string) => `${supportMailboxPath(mailboxId)}/agents`;
const supportMailboxSavedRepliesPath = (mailboxId: string) => `${supportMailboxPath(mailboxId)}/saved-replies`;
const supportMailboxGitHubIdentitiesPath = (mailboxId: string) => `${supportMailboxPath(mailboxId)}/githubIdentities`;

export interface SupportNotificationFeedParams {
	page: number;
	/** Server caps at 50. */
	pageSize: number;
	mailboxId?: string;
}

/**
 * Reporting window for the mailbox reports overview endpoint. `start`/`end` are ISO-8601 UTC
 * instants ([start, end)); `timezone` is the IANA zone the API buckets the daily series in.
 */
export interface SupportReportOverviewParams {
	start: string;
	end: string;
	timezone: string;
}

export const SUPPORT_QUERY_KEYS = {
	notifications: {
		all: () => ['support-notifications'] as const,
		feed: (params: SupportNotificationFeedParams) => [...SUPPORT_QUERY_KEYS.notifications.all(), params] as const,
		unreadCount: () => [...SUPPORT_QUERY_KEYS.notifications.all(), 'unread-count'] as const,
	},
	tickets: {
		lists: () => ['support-tickets'] as const,
		list: (params: SupportTicketListParams) => [...SUPPORT_QUERY_KEYS.tickets.lists(), params] as const,
		search: (params: SupportTicketSearchParams) => [...SUPPORT_QUERY_KEYS.tickets.lists(), 'search', params] as const,
		detail: (ticketId: string) => ['support-ticket', ticketId] as const,
		threads: (ticketId: string) => ['support-threads', ticketId] as const,
		events: (ticketId: string) => ['support-ticket-events', ticketId] as const,
		github: (ticketId: string) => ['support-ticket-github', ticketId] as const,
		githubIssueRuns: (ticketId: string, linkId: string) =>
			['support-ticket-github-issue-runs', ticketId, linkId] as const,
		userSearch: (query: string) => ['support-ticket-user-search', query] as const,
		recipientSuggestions: (ticketId: string, query: string) =>
			['support-ticket-recipient-suggestions', ticketId, query] as const,
	},
	mailboxes: {
		lists: () => ['support-mailboxes'] as const,
		detail: (mailboxId: string) => ['support-mailbox', mailboxId] as const,
		categories: (mailboxId: string) => ['support-categories', mailboxId] as const,
		agentConfigs: (mailboxId: string) => ['support-agent-configs', mailboxId] as const,
		agentTicketCountsAll: () => ['support-agent-ticket-counts'] as const,
		agentTicketCounts: (mailboxId: string) =>
			[...SUPPORT_QUERY_KEYS.mailboxes.agentTicketCountsAll(), mailboxId] as const,
		myAgentConfig: (mailboxId: string) => ['support-my-agent-config', mailboxId] as const,
		availableUsers: (mailboxId: string, query: string) => ['support-available-users', mailboxId, query] as const,
		userAccess: (mailboxId: string, userId: string) => ['support-mailbox-user-access', mailboxId, userId] as const,
		savedReplies: (mailboxId: string) => ['support-saved-replies', mailboxId] as const,
		// The mappings are global; the mailbox is the authorization scope the route reads them through.
		gitHubIdentities: (mailboxId: string) => ['support-github-identities', mailboxId] as const,
	},
	reports: {
		all: () => ['support-reports'] as const,
		overview: (mailboxId: string | null, params: SupportReportOverviewParams) =>
			[...SUPPORT_QUERY_KEYS.reports.all(), 'overview', mailboxId ?? 'all', params] as const,
	},
} as const;

export const SUPPORT_PATHS = {
	notifications: {
		feed: (params: SupportNotificationFeedParams) => {
			const queryParams = new URLSearchParams();
			queryParams.set('page', String(params.page));
			queryParams.set('pageSize', String(params.pageSize));
			appendIfPresent(queryParams, 'mailboxId', params.mailboxId);
			return withQueryString('support/notifications', queryParams);
		},
		unreadCount: 'support/notifications/unreadCount',
		read: (notificationId: string) => `support/notifications/${encodePathSegment(notificationId)}/read`,
		/** Omitting ticketId marks every notification read. */
		readBatch: (ticketId?: string) => {
			const queryParams = new URLSearchParams();
			appendIfPresent(queryParams, 'ticketId', ticketId);
			return withQueryString('support/notifications/read', queryParams);
		},
	},
	tickets: {
		list: (params: SupportTicketListRequestParams) => {
			const queryParams = new URLSearchParams();
			queryParams.set('pageSize', String(params.pageSize));
			appendIfPresent(queryParams, 'pageToken', params.pageToken);
			appendIfPresent(queryParams, 'sort', params.sort);
			appendIfPresent(queryParams, 'sortBy', params.sortBy);
			return withQueryString('support/tickets/filter', queryParams);
		},
		search: (params: SupportTicketSearchParams) => {
			const queryParams = new URLSearchParams();
			appendIfPresent(queryParams, 'query', params.query);
			appendIfPresent(queryParams, 'mailboxId', params.mailboxId);
			appendIfPresent(queryParams, 'accountId', params.accountId);
			appendIfPresent(queryParams, 'status', params.status);
			appendIfPresent(queryParams, 'priority', params.priority);
			appendIfPresent(queryParams, 'categoryId', params.categoryId);
			appendIfPresent(queryParams, 'assigneeId', params.assigneeId);
			appendIfPresent(queryParams, 'csmId', params.csmId);
			queryParams.set('page', String(params.page));
			queryParams.set('pageSize', String(params.pageSize));
			return withQueryString('support/tickets/search', queryParams);
		},
		account: (ticketId: string) => `${supportTicketsPath(ticketId)}/account`,
		create: 'support/tickets',
		detail: supportTicketsPath,
		status: (ticketId: string) => `${supportTicketsPath(ticketId)}/status`,
		snooze: (ticketId: string) => `${supportTicketsPath(ticketId)}/snooze`,
		assign: (ticketId: string) => `${supportTicketsPath(ticketId)}/assign`,
		priority: (ticketId: string) => `${supportTicketsPath(ticketId)}/priority`,
		category: (ticketId: string) => `${supportTicketsPath(ticketId)}/category`,
		mailbox: (ticketId: string) => `${supportTicketsPath(ticketId)}/mailbox`,
		customer: (ticketId: string) => `${supportTicketsPath(ticketId)}/customer`,
		events: (ticketId: string) => `${supportTicketsPath(ticketId)}/events`,
		merge: (targetTicketId: string, sourceTicketId: string) =>
			`${supportTicketsPath(targetTicketId)}/merge/${encodePathSegment(sourceTicketId)}`,
		githubIssueMarkdown: (ticketId: string) => `${supportTicketsPath(ticketId)}/github-issue-markdown`,
		userSearch: (query: string) => {
			const queryParams = new URLSearchParams();
			queryParams.set('query', query.trim());
			return withQueryString('support/tickets/users/search', queryParams);
		},
		recipientSuggestions: (ticketId: string, query: string) => {
			const queryParams = new URLSearchParams();
			queryParams.set('query', query.trim());
			return withQueryString(`${supportTicketsPath(ticketId)}/recipientSuggestions`, queryParams);
		},
	},
	threads: {
		list: supportTicketThreadsPath,
		detail: supportTicketThreadPath,
		status: (ticketId: string, threadId: string) => `${supportTicketThreadPath(ticketId, threadId)}/status`,
		deepDive: (ticketId: string) => `${supportTicketThreadsPath(ticketId)}/deep-dive`,
		refine: (ticketId: string) => `${supportTicketThreadsPath(ticketId)}/refine`,
		send: (ticketId: string, threadId: string) => `${supportTicketThreadPath(ticketId, threadId)}/send`,
		forward: (ticketId: string, threadId: string) => `${supportTicketThreadPath(ticketId, threadId)}/forward`,
		attachmentsZip: (ticketId: string, threadId: string) =>
			`${supportTicketThreadPath(ticketId, threadId)}/attachments/zip`,
	},
	github: {
		panel: (ticketId: string) => `${supportTicketsPath(ticketId)}/github`,
		linkIssue: (ticketId: string) => `${supportTicketGitHubIssuesPath(ticketId)}/link`,
		issues: supportTicketGitHubIssuesPath,
		issue: supportTicketGitHubIssuePath,
		syncIssue: (ticketId: string, linkId: string) => `${supportTicketGitHubIssuePath(ticketId, linkId)}/sync`,
		escalateIssue: (ticketId: string, linkId: string) => `${supportTicketGitHubIssuePath(ticketId, linkId)}/escalate`,
		issueRuns: (ticketId: string, linkId: string) => `${supportTicketGitHubIssuePath(ticketId, linkId)}/runs`,
	},
	mailboxes: {
		list: 'support/mailboxes',
		detail: supportMailboxPath,
		categories: supportMailboxCategoriesPath,
		category: (mailboxId: string, categoryId: string) =>
			`${supportMailboxCategoriesPath(mailboxId)}/${encodePathSegment(categoryId)}`,
		agents: supportMailboxAgentsPath,
		gitHubIdentities: supportMailboxGitHubIdentitiesPath,
		gitHubIdentity: (mailboxId: string, userId: string) =>
			`${supportMailboxGitHubIdentitiesPath(mailboxId)}/${encodePathSegment(userId)}`,
		myAgent: (mailboxId: string) => `${supportMailboxAgentsPath(mailboxId)}/me`,
		agentTicketCounts: (mailboxId: string) => `${supportMailboxAgentsPath(mailboxId)}/ticketCounts`,
		agent: (mailboxId: string, agentConfigId: string) =>
			`${supportMailboxAgentsPath(mailboxId)}/${encodePathSegment(agentConfigId)}`,
		availableUsers: (mailboxId: string, query: string) => {
			const queryParams = new URLSearchParams();
			queryParams.set('q', query.trim());
			return withQueryString(`${supportMailboxAgentsPath(mailboxId)}/available-users`, queryParams);
		},
		userAccess: (mailboxId: string, userId: string) =>
			`${supportMailboxAgentsPath(mailboxId)}/access/${encodePathSegment(userId)}`,
		savedReplies: supportMailboxSavedRepliesPath,
		savedReply: (mailboxId: string, savedReplyId: string) =>
			`${supportMailboxSavedRepliesPath(mailboxId)}/${encodePathSegment(savedReplyId)}`,
	},
	reports: {
		/** `mailboxId: null` targets the admin-gated all-mailboxes overview. */
		overview: (mailboxId: string | null, params: SupportReportOverviewParams) => {
			const queryParams = new URLSearchParams();
			queryParams.set('start', params.start);
			queryParams.set('end', params.end);
			queryParams.set('timezone', params.timezone);
			const path =
				mailboxId === null ? 'support/reports/overview' : `${supportMailboxPath(mailboxId)}/reports/overview`;
			return withQueryString(path, queryParams);
		},
	},
} as const;

/** Shared polling cadence for the ticket-detail surfaces (detail, threads, events, GitHub). */
export const SUPPORT_POLL_INTERVAL_MS = 10_000;

/** react-query polling options shared by the ticket-detail surfaces. */
export function supportPollingOptions(refetchInterval: number | false) {
	return {
		refetchInterval,
		refetchIntervalInBackground: false,
		refetchOnWindowFocus: true,
		// While polling, the next cycle is already a retry — don't stack the full retry
		// policy on every failed poll. Non-polling mounts keep the standard policy.
		retry: refetchInterval === false ? supportQueryRetry : supportPollingRetry,
	};
}

/** Unwrap an API response that is either a bare array or a paged `{ values }` envelope. */
export function unwrapValues<T>(response: T[] | { values?: T[] } | null | undefined): T[] {
	if (Array.isArray(response)) return response;
	return response?.values ?? [];
}

/**
 * Invalidate every inbox list view that can display a ticket row, and the per-agent workload counts
 * alongside them — assigning, closing or moving a ticket changes both, and the counts are read from
 * the same screens.
 */
export function invalidateTicketLists(queryClient: QueryClient) {
	queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.lists() });
	queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.agentTicketCountsAll() });
}

/**
 * Invalidate a single ticket's detail and its activity-events timeline. Every ticket
 * mutation records a structured event, so the events query must be refreshed alongside
 * the detail or the timeline lags until the next poll.
 */
export function invalidateTicket(queryClient: QueryClient, ticketId: string) {
	queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.detail(ticketId) });
	queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.events(ticketId) });
}

/** Invalidate a ticket's threads and detail after a thread is added, sent, or forwarded. */
export function invalidateTicketThreads(queryClient: QueryClient, ticketId: string) {
	queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.threads(ticketId) });
	queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.detail(ticketId) });
}
